import type { Transition } from 'framer-motion';

/**
 * Shared motion constants for the "chat docks to header" interaction.
 *
 * The chat panel shell and the header chat launcher are two states of the
 * same object: both render a framer-motion element with
 * `layoutId={CHAT_DOCK_LAYOUT_ID}` inside MainLayout's <LayoutGroup>, so
 * mounting one as the other unmounts produces the shared-element flight.
 */

/** layoutId shared by the flight surface (ChatDockFlight) and HeaderChatLauncher. */
export const CHAT_DOCK_LAYOUT_ID = 'chat-surface';

/** layoutId shared by the Brief flight surface (BriefDockFlight) and the header
 *  bell (HeaderBriefLauncher). The Daily Brief dock mirrors the chat dock's
 *  shared-element morph: the bell grows into the Brief panel on open and the
 *  panel shrinks back into the bell on close. It REUSES the chat dock's spring
 *  (CHAT_DOCK_SPRING) and land clock (CHAT_DOCK_UNDOCK_LAND_MS) VERBATIM in both
 *  directions, so the Brief dock opens/closes at the exact same pace as the chat
 *  dock. See BriefDockFlight.tsx / HeaderBriefLauncher.tsx. */
export const BRIEF_DOCK_LAYOUT_ID = 'brief-surface';

/** Viewport-relative rect for the flight surface's resting position. */
export interface DockRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * The flight spring: quick (~0.19s envelope, ~20% faster than the original
 * 420/30/0.9 after live-QA feedback) with a slight overshoot so the launcher
 * "lands" with a small bounce without wobbling.
 */
export const CHAT_DOCK_SPRING: Transition = {
  type: 'spring',
  stiffness: 600,
  damping: 36,
  mass: 0.85,
};

/**
 * Liftoff gate: how long 'docking' holds before the flight launches (ms).
 * Must comfortably contain the 50ms content fade and the 60ms glass fade-in
 * (`.chat-dock-docking` rules in ChatBox.css + ChatDockFlight's opacity) —
 * the box lifts off the moment it has fully formed.
 */
export const CHAT_DOCK_CONTENT_FADE_MS = 70;

/**
 * Safety net for the transitional dock states: if an animation-complete
 * callback never fires (unmounted mid-flight, dropped frame, etc.) the
 * state machine force-settles after this long so the UI can never wedge
 * in 'docking'/'undocking'.
 */
export const CHAT_DOCK_SETTLE_FALLBACK_MS = 900;

/**
 * Undock landing clock (ms). The spring (600/36/0.85) has visually arrived
 * — overshoot and return included — by ~230ms, but framer's
 * onLayoutAnimationComplete waits for full rest detection, which lags the
 * eye by another 100-300ms. Landing on this fixed clock starts the content
 * reveal the moment the box LOOKS settled instead of leaving the empty
 * glass lingering at its final rect. The animation callback still races it
 * (whichever fires first wins; finishUndock is state-guarded), and
 * CHAT_DOCK_SETTLE_FALLBACK_MS remains the can't-wedge backstop elsewhere.
 */
export const CHAT_DOCK_UNDOCK_LAND_MS = 230;

/**
 * How long the post-undock landing window stays open (ms). While it's open
 * ChatBox wears `.chat-dock-landing`, which swaps the content reveal from
 * the slide-in entrance to a plain fast fade (the flight already supplied
 * the motion). Must outlast the 160ms reveal in ChatBox.css — the class
 * dropping afterwards only reverts custom properties, which never restarts
 * a CSS animation.
 */
export const CHAT_DOCK_LANDING_REVEAL_MS = 320;
